import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatCents } from "@/services/finance";
import { vehicleOpsApi, type VehicleExpense } from "@/services/vehicleOps";
import { listDrivers } from "@/services/drivers";
import { useAuthStore } from "@/store/authStore";
import { useCan } from "@/auth/permissions";
import { PageShell, LoadingState } from "@/shared/ui/PageHeader";
import { Card } from "@/shared/ui/Card";

const CATEGORY_LABEL: Record<string, string> = {
  fuel: "⛽ 油费",
  meal: "🍱 餐饮",
  parking: "🅿️ 停车",
  toll: "🛣️ 过路费",
  fine: "💳 罚款",
  vehicle_maintenance: "🔧 维修",
  accommodation: "🏨 住宿",
  other: "其他",
};

const STATUS_TABS: { key: string; label: string }[] = [
  { key: "all", label: "全部状态" },
  { key: "pending", label: "待审" },
  { key: "escalated", label: "待老板特批" },
  { key: "approved", label: "已通过" },
  { key: "paid", label: "已发放" },
  { key: "rejected", label: "已驳回" },
];
const STATUS_TONE: Record<string, { label: string; cls: string }> = {
  pending: { label: "待审", cls: "bg-amber-100 text-amber-700" },
  escalated: { label: "待老板特批", cls: "bg-violet-100 text-violet-700" },
  approved: { label: "已通过", cls: "bg-emerald-100 text-emerald-700" },
  paid: { label: "已发放", cls: "bg-blue-100 text-blue-700" },
  rejected: { label: "已驳回", cls: "bg-rose-100 text-rose-600" },
};

/** 财务 · 费用报销查询（/admin/finance/expenses）。
 *  司导报销 + 车务费用统一查询入口·财务专属（OP/司导端不可见）。
 *  支持按「司导/提交人」筛选 —— 司导工资及报销历史从司导管理迁来此处。 */
export function FinanceExpensesPage() {
  const qc = useQueryClient();
  const [submitter, setSubmitter] = useState<string>("all"); // user_id | "all"
  const [status, setStatus] = useState<string>("all");

  // 司导报销（origin=driver_reimbursement）· B 方案分流·车务费用归车务台账不在此
  const allQ = useQuery({
    queryKey: ["finance", "expenses", "driver_reimbursement"],
    queryFn: () => vehicleOpsApi.listExpensesFiltered({ origin: "driver_reimbursement" }),
  });
  const driversQ = useQuery({
    queryKey: ["drivers", "list", "for-expenses"],
    queryFn: () => listDrivers({ limit: 200 }),
  });

  const all = allQ.data ?? [];
  const drivers = driversQ.data ?? [];
  // user_id → 司导名（用于行内显示提交人 + 下拉选项）
  const driverByUserId = new Map(
    drivers.filter((d) => d.user_id).map((d) => [String(d.user_id), d.name_cn]),
  );
  const driverOptions = drivers.filter((d) => d.user_id);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["finance", "expenses"] });
    qc.invalidateQueries({ queryKey: ["finance", "overview"] });
  };
  const approve = useMutation({
    mutationFn: (e: VehicleExpense) => vehicleOpsApi.approveExpense(e.id),
    onSuccess: invalidate,
    onError: (err) => alert((err as Error).message || "审批失败"),
  });
  const reject = useMutation({
    mutationFn: (e: VehicleExpense) => {
      const reason = prompt("驳回理由（必填·进审计日志）：")?.trim();
      if (!reason) return Promise.reject(new Error("已取消"));
      return vehicleOpsApi.rejectExpense(e.id, reason);
    },
    onSuccess: invalidate,
    onError: (err) => {
      if ((err as Error).message !== "已取消") alert((err as Error).message || "驳回失败");
    },
  });
  const escalate = useMutation({
    mutationFn: (e: VehicleExpense) => {
      const reason = prompt("提特批理由（必填·升级给老板终批·进审计）：")?.trim();
      if (!reason) return Promise.reject(new Error("已取消"));
      return vehicleOpsApi.escalateExpense(e.id, reason);
    },
    onSuccess: invalidate,
    onError: (err) => {
      if ((err as Error).message !== "已取消") alert((err as Error).message || "提特批失败");
    },
  });

  // 老板/技术管理员 = 可终批特批工单
  const canDecide = useCan("vehicle_ops.expenses.approve");
  const me = useAuthStore((s) => s.user);
  const isOwner =
    !!me &&
    ((me.roleCodes ?? []).some((r) => r === "owner" || r === "tech_admin") || me.role === "owner" || me.role === "tech_admin");

  const rows = all
    .filter((e) => submitter === "all" || e.submitter_user_id === submitter)
    .filter((e) => status === "all" || e.status === status);

  const sum = (list: VehicleExpense[]) => list.reduce((s, e) => s + e.amount_cents, 0);
  const kpis = [
    { label: "报销总额", value: formatCents(sum(all)), sub: `${all.length} 笔`, color: "text-slate-800" },
    { label: "待审", value: formatCents(sum(all.filter((e) => e.status === "pending"))), sub: `${all.filter((e) => e.status === "pending").length} 笔待审`, color: "text-amber-500" },
    { label: "已通过", value: formatCents(sum(all.filter((e) => e.status === "approved"))), sub: "待发放", color: "text-emerald-500" },
    { label: "已发放", value: formatCents(sum(all.filter((e) => e.status === "paid"))), sub: "本期支出", color: "text-blue-500" },
  ];

  if (allQ.isLoading) return <LoadingState />;
  if (allQ.isError) return (
    <div className="flex flex-col items-center justify-center gap-2 p-12">
      <p className="text-[13px] font-semibold text-red-700">费用报销加载失败</p>
      <p className="text-[11px] text-red-500">{(allQ.error as Error)?.message || "网络错误"}</p>
      <button onClick={() => allQ.refetch()} disabled={allQ.isFetching} className="mt-1 rounded-md bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">{allQ.isFetching ? "重试中…" : "点击重试"}</button>
    </div>
  );

  return (
    <PageShell
      title="🧾 司导报销 · Reimbursements"
      subtitle="司导费用报销查询 · 按司导筛选 · 财务专属（车务费用归车务台账）"
    >
      <div className="rounded-lg bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-800">🔒 仅老板 + 财务可见 · 司导报销历史已从司导管理迁至此处（OP 不可见）</div>

      {/* 4 KPI */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[11px] font-semibold text-slate-500">{k.label}</div>
            <div className={`mt-1.5 text-[22px] font-bold leading-none ${k.color}`}>{k.value}</div>
            <div className="mt-1.5 text-[10px] text-slate-400">{k.sub}</div>
          </div>
        ))}
      </div>

      <Card>
        {/* 筛选条：司导下拉 + 状态 tab */}
        <div className="mb-3 flex flex-wrap items-center gap-3 border-b border-slate-100 pb-3">
          <label className="flex items-center gap-2 text-[12px] text-slate-600">
            <span className="font-semibold">👤 司导/提交人</span>
            <select
              value={submitter}
              onChange={(e) => setSubmitter(e.target.value)}
              className="h-8 rounded-lg border border-slate-300 px-2 text-[12px] text-slate-700"
            >
              <option value="all">全部提交人</option>
              {driverOptions.map((d) => (
                <option key={d.id} value={String(d.user_id)}>{d.name_cn}</option>
              ))}
            </select>
          </label>
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_TABS.map((t) => (
              <button key={t.key} onClick={() => setStatus(t.key)} className={`h-8 rounded-lg px-3 text-[12px] ${status === t.key ? "bg-blue-600 font-semibold text-white" : "font-medium text-slate-700 hover:bg-slate-100"}`}>{t.label}</button>
            ))}
          </div>
          <span className="ml-auto text-[11px] text-slate-500">共 {rows.length} 笔 · 合计 {formatCents(sum(rows))}</span>
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] text-slate-500">
                <th className="px-3 py-2 font-medium">日期</th>
                <th className="px-3 py-2 font-medium">提交人</th>
                <th className="px-3 py-2 font-medium">类别</th>
                <th className="px-3 py-2 font-medium">单号</th>
                <th className="px-3 py-2 font-medium">说明</th>
                <th className="px-3 py-2 text-right font-medium">金额</th>
                <th className="px-3 py-2 font-medium">状态</th>
                <th className="px-3 py-2 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((e) => {
                const tone = STATUS_TONE[e.status] ?? { label: e.status, cls: "bg-slate-100 text-slate-600" };
                const who = e.submitter_user_id ? (driverByUserId.get(e.submitter_user_id) ?? "其他员工") : "—";
                return (
                  <tr key={e.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-700">{e.receipt_date}</td>
                    <td className="px-3 py-2 font-medium text-slate-800">{who}</td>
                    <td className="px-3 py-2 text-slate-700">{CATEGORY_LABEL[e.category] ?? e.category}</td>
                    <td className="px-3 py-2 text-slate-500">{e.receipt_number}</td>
                    <td className="px-3 py-2 text-slate-600">
                      {e.description ?? "—"}
                      {e.status === "escalated" && e.escalation_reason && (
                        <span className="mt-0.5 block text-[11px] text-violet-600">⤴ 特批理由：{e.escalation_reason}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-slate-800">{formatCents(e.amount_cents)}</td>
                    <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[11px] ${tone.cls}`}>{tone.label}</span></td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1.5">
                        {e.status === "pending" && canDecide && (
                          <>
                            <button onClick={() => reject.mutate(e)} disabled={reject.isPending} className="rounded-md border border-slate-300 px-2 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40">驳回</button>
                            <button onClick={() => escalate.mutate(e)} disabled={escalate.isPending} className="rounded-md border border-violet-300 px-2 py-1 text-[12px] font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-40">提特批</button>
                            <button onClick={() => approve.mutate(e)} disabled={approve.isPending} className="rounded-md bg-blue-600 px-2 py-1 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">通过</button>
                          </>
                        )}
                        {e.status === "escalated" && (isOwner && canDecide ? (
                          <>
                            <button onClick={() => reject.mutate(e)} disabled={reject.isPending} className="rounded-md border border-slate-300 px-2 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40">驳回</button>
                            <button onClick={() => approve.mutate(e)} disabled={approve.isPending} className="rounded-md bg-violet-600 px-2 py-1 text-[12px] font-semibold text-white hover:bg-violet-700 disabled:opacity-40">特批通过</button>
                          </>
                        ) : (
                          <span className="text-[11px] text-violet-500">待老板终批</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400">该筛选条件下暂无报销记录</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </PageShell>
  );
}
